import React from "react";
import Badge from "../Badge/Badge";
import VehicleTypeIcon from "./vehicleTypeIcon";
import { driverName, tipoLabel, VehicleInsideRow } from "./utils/vehicleInsideRow";
import type { AssociatedVehicleInside } from "../../../../passapp-sdk/types/userTypes";

/**
 * Celdas propias de la vista "por vehículo". Las que comparte con la vista por persona (fecha,
 * persona, anfitrión, botón de salida) siguen viviendo en GuardiaCells.
 *
 * Criterio de badges: sólo se marca la excepción. Todo lo que está en el listado está dentro —las
 * unidades que salen desaparecen—, así que un badge "Dentro" en cada fila no aporta nada y tapa lo
 * único que el guardia necesita ver de un vistazo: qué unidad se quedó sin su chofer.
 */

export const TipoCell: React.FC<{ row: VehicleInsideRow }> = ({ row }) => (
  <span className="gv-tipo">
    <VehicleTypeIcon role={row.role} vehicleTypeName={row.vehicleTypeName} />
    <span className="gv-tipo-label">{tipoLabel(row)}</span>
  </span>
);

/**
 * Patente de una unidad.
 *
 * Con `inside` la patente se pinta con su punto de estado —verde dentro, amarillo fuera— y el
 * borde apagado de la unidad que ya salió; el `title` repite el estado en texto para no dejarlo
 * sólo en el color. Sin `inside` es una patente a secas: así se muestran las de los ingresos
 * viejos, donde no hay dato de si la unidad sigue adentro, e inventarles un estado sería peor que
 * no mostrarlo.
 */
export const PatenteChip: React.FC<{
  patente?: string;
  inside?: boolean;
  className?: string;
}> = ({ patente, inside, className }) => {
  if (!patente) return <span className="gv-empty">—</span>;

  const classes = ["gv-plate", className, inside === false ? "gv-plate-out" : undefined]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      className={classes}
      title={
        inside === undefined ? undefined : inside ? "Dentro del complejo" : "Fuera del complejo"
      }
    >
      {inside !== undefined && <i className={inside ? "gv-dot gv-dot-in" : "gv-dot gv-dot-out"} />}
      {patente}
    </span>
  );
};

/** Unidad que entró con esta: la misma patente, en el cuerpo chico de la columna de asociadas. */
export const AsociadaChip: React.FC<{ unit: AssociatedVehicleInside }> = ({ unit }) => (
  <PatenteChip patente={unit.plate} inside={unit.inside} className="gv-plate-trailer" />
);

/** Unidades que entraron con esta, cada una con su propio estado. */
export const AsociadasCell: React.FC<{ asociadas: AssociatedVehicleInside[] }> = ({
  asociadas,
}) => {
  if (!asociadas || asociadas.length === 0) return <span className="gv-empty">—</span>;
  return (
    <div className="gv-asociadas">
      {asociadas.map((unit, index) => (
        <AsociadaChip key={unit.plate ?? index} unit={unit} />
      ))}
    </div>
  );
};

/**
 * Chofer que ingresó la unidad. El badge aparece sólo si ya no está: es el único aviso de la fila
 * y va acompañado del borde ámbar.
 */
export const ChoferCell: React.FC<{ row: VehicleInsideRow }> = ({ row }) => {
  const nombre = driverName(row.driver);
  if (!nombre) return <span className="gv-empty">—</span>;
  return (
    <div className="gv-chofer">
      <span className="gv-chofer-nombre">{nombre}</span>
      {row.driver?.dni && <span className="gv-chofer-dni">DNI: {row.driver.dni}</span>}
      {!row.driverInside && <Badge label="Sin chofer" variant="yellow" />}
    </div>
  );
};

export const VerMasButton: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <button type="button" className="gv-link-more" onClick={onClick}>
    Ver más
  </button>
);
